import React, {useEffect, useState, useRef, useMemo, useLayoutEffect, useCallback} from 'react';
import {default as ReactFusioncharts} from 'react-fusioncharts';
import {reportGeneratorsStore} from '../../stores/ReportGeneratorsStore';
import {createChartConfig} from '../../services/chartService';
import {observer} from 'mobx-react-lite';
import {KPI} from './Kpi';
import {SingleSeriesChartTypesOptions} from '../../chartConsts';
import {ChartConfig} from '../../types';
import {reportWebSdkService} from '../../services/reportWebSdkService';
import {CircularLoader} from '../../../../components/CircularLoader/CircularLoader';
import {noop} from '@priority-software/priority-style-react';
import {prepareInputsData} from './utils';
import {useChartContext} from '../../contexts/useChartContext';
import {ChartErrorBoundary} from './ChartErrorBoundary';
import {EmptyState} from './EmptyState';

const Chart = observer(() => {
    const currentScreen = reportGeneratorsStore?.currentScreen;
    const {currentReport} = currentScreen;
    const [config, setConfig] = useState<ChartConfig>(null);
    const [isLoadingExplanatoryReport, setIsLoading] = useState<boolean>(false);
    // We're using setAggregatedData but not using aggregatedData directly
    const [, setAggregatedData] = useState<any>(null);
    const chartRef = useRef<any>(null);

    // Store which reports have no data to avoid re-rendering FusionCharts for them
    const noDataReportsRef = useRef<Set<string>>(new Set());

    // Use the chart context to share data with other components
    const {
        setChartConfig,
        setChartData,
        setAggregatedChartData,
        showAnalysisPanel,
        setHasNoData,
        hasNoData
    } = useChartContext();

    // Create a stable key based only on report ID to prevent unnecessary re-renders
    // For no-data reports, add timestamp to force fresh rendering
    const chartKey = useMemo(() => {
        const reportId = currentReport?.reportId || 'no-report';
        const isNoDataReport = noDataReportsRef.current.has(reportId.toString());
        return isNoDataReport ? `chart-${reportId}-nodata-${Date.now()}` : `chart-${reportId}`;
    }, [currentReport?.reportId, hasNoData]);

    // Reference to the chart container element
    const chartContainerRef = useRef<HTMLDivElement>(null);

    // Track the container dimensions
    const [containerDimensions, setContainerDimensions] = useState({width: 0, height: 0});

    const onDataPlotClick = async (config: ChartConfig) => {
        setIsLoading(true);
        const reportInputsMetaData = prepareInputsData(config);
        currentScreen.setExpReportInputsMetaData(reportInputsMetaData);
        await reportWebSdkService.startProc();
        setIsLoading(false);
    };

    // Callback to handle no data state
    const handleNoDataToDisplay = useCallback(() => {
        const reportId = currentReport?.reportId?.toString();
        if (reportId) {
            noDataReportsRef.current.add(reportId);
        }

        // Dispose any existing chart instance immediately
        if (chartRef.current && chartRef.current.chartObj) {
            try {
                chartRef.current.chartObj.dispose();
            } catch (error) {
                console.warn('Error disposing chart on no data:', error);
            }
            chartRef.current = null;
        }

        // Don't clear config - we need it for the empty state to render
        setHasNoData(true);
    }, [setHasNoData, currentReport?.reportId]);

    const getChartInfo = async () => {
        const chartConfig = await currentScreen?.getChartConfig(currentReport?.reportId.toString());
        if (chartConfig) {
            chartConfig.data = currentScreen?.getChartData;
            setConfig(chartConfig);
            // Reset no data state when new chart config is loaded
            setHasNoData(false);
            // Share the chart config and data with the context
            setChartConfig(chartConfig);
            setChartData(currentScreen?.getChartData);
        }
    };

    // Extract aggregated data from the chart
    const extractAggregatedData = (chartObj: any) => {
        // More defensive checks
        if (!chartObj || !chartObj.getJSONData || !chartRef.current) {
            console.warn('Chart object not ready for data extraction');
            return undefined;
        }

        try {
            // Check if chart is still valid before extracting data
            if (chartRef.current && chartRef.current.chartObj === chartObj) {
                const jsonData = chartObj.getJSONData();
                if (jsonData) {
                    const extractedData = {
                        data: jsonData
                    };

                    // Update state and context
                    setAggregatedData(extractedData);
                    setAggregatedChartData(extractedData);
                }
            } else {
                console.warn('Chart reference mismatch, skipping data extraction');
            }
        } catch (error) {
            console.error('Error extracting aggregated data:', error);
        }
    };

    useEffect(() => {
        // Check if this report is known to have no data
        const reportId = currentReport?.reportId?.toString();
        if (reportId && noDataReportsRef.current.has(reportId)) {
            setHasNoData(true);
            // Still get chart info to set config so empty state can render
            getChartInfo();
            return;
        }

        // Explicitly dispose chart when switching reports/sessions
        if (chartRef.current && chartRef.current.chartObj) {
            try {
                chartRef.current.chartObj.dispose();
            } catch (error) {
                console.warn('Error disposing chart for report change:', error);
            }
            chartRef.current = null;
        }

        getChartInfo();
    }, [currentReport]);

    // Extract aggregated data when chart is rendered
    useEffect(() => {
        if (chartRef.current && chartRef.current.chartObj) {
            // Wait for chart to fully render
            setTimeout(() => {
                extractAggregatedData(chartRef.current?.chartObj);
            }, 500);
        }
    }, [config]);

    // Handle panel visibility changes and resize events
    useEffect(() => {
        // Give the DOM time to update after panel visibility changes
        const resizeTimeout = setTimeout(() => {
            if (chartRef.current && chartRef.current.chartObj) {
                chartRef.current.chartObj.resizeTo();
            }
        }, 300);

        return () => clearTimeout(resizeTimeout);
    }, [showAnalysisPanel]);

    // Main cleanup effect for component unmount only
    useEffect(() => {
        return () => {
            // Dispose the chart when component unmounts
            if (chartRef.current && chartRef.current.chartObj) {
                try {
                    chartRef.current.chartObj.dispose();
                } catch (error) {
                    console.warn('Error disposing chart on unmount:', error);
                }
                chartRef.current = null;
            }
        };
    }, []);

    // Set up resize observer to detect container size changes
    useLayoutEffect(() => {
        if (!chartContainerRef.current) return undefined;

        // Create a ResizeObserver to monitor container size changes
        const resizeObserver = new ResizeObserver(entries => {
            for (const entry of entries) {
                const {width, height} = entry.contentRect;

                // Only update if dimensions actually changed
                if (width !== containerDimensions.width || height !== containerDimensions.height) {
                    setContainerDimensions({width, height});

                    // Resize the chart if it exists
                    if (chartRef.current && chartRef.current.chartObj) {
                        chartRef.current.chartObj.resizeTo();
                    }
                }
            }
        });

        // Start observing the container
        resizeObserver.observe(chartContainerRef.current);

        // Clean up observer on unmount
        return () => resizeObserver.disconnect();
    }, []);

    if (!config) {
        return null;
    }

    config.dataPlotEventHandler = config.explanatoryReport ? () => onDataPlotClick(config) : noop;
    if (config['type'] === SingleSeriesChartTypesOptions.kpi) {
        return <KPI config={config} />;
    } else if (hasNoData) {
        return <EmptyState />;
    } else {
        const finalConfig = createChartConfig(config, handleNoDataToDisplay);
        return (
            <>
                {/* Chart container div with ref for resize detection */}
                <div
                    ref={chartContainerRef}
                    style={{width: '100%', height: '90%'}}
                    id={`chart-container-${currentReport?.reportId || 'default'}`}
                >
                    <ChartErrorBoundary onError={handleNoDataToDisplay}>
                        {/* @ts-ignore - ReactFusioncharts is a valid JSX component */}
                        <ReactFusioncharts
                            key={chartKey}
                            {...finalConfig}
                            ref={chartRef}
                            onRender={chartObj => {
                                try {
                                    // Store reference to chart object
                                    chartRef.current = {chartObj};
                                    // Extract data after chart is rendered
                                    setTimeout(() => extractAggregatedData(chartObj), 500);
                                } catch (error) {
                                    console.error('Error in onRender callback:', error);
                                    handleNoDataToDisplay();
                                }
                            }}
                        />
                    </ChartErrorBoundary>
                </div>
                {isLoadingExplanatoryReport && (
                    <div style={{position: 'absolute'}}>
                        <CircularLoader />
                    </div>
                )}
            </>
        );
    }
});

export default Chart;
